import { call, put, select, takeLatest, takeEvery } from 'redux-saga/effects';
import { HubConnectionBuilder, LogLevel } from '@microsoft/signalr';
import { isConnectionReady } from 'utils/connection-helper';
import { SEND_FEEDBACK, SETUP_SIGNALR_CONNECTION, SEND_FLAG, SEND_EDGE_FLAG } from './constants';
import { hideFeedbackModal, setConnection, setFeedBackError, hideFlagModal, setFlagError, hideEdgeFlagModal, setEdgeFlagError } from './actions';
import { selectConnection } from './selectors';
import { selectFilters } from 'store/filters/selectors';
import { setDataDate } from './connection';
import { selectGraph, selectContextIds, selectContextsObject } from 'store/graph/selectors';
import { getValidAccessToken } from 'utils/token-refresh';

const setupConnection = async (token) => {
  try {
    const conn = new HubConnectionBuilder()
      .withUrl(`${process.env.REACT_APP_BASE_API_URL}/graph`, {
        accessTokenFactory: () => {
          // Returns current token or silently refreshes if near expiry
          return getValidAccessToken();
        },
      })
      .configureLogging(LogLevel.Information)
      .withAutomaticReconnect()
      .build();

    conn.serverTimeoutInMilliseconds = 120000;

    conn.on('statusNotification', (model) => {
      // statusNotification received (log removed)
    });

    conn.on('setDataFreshDate', (date) => {
      setDataDate(date);
    });

    await conn.start();
    return conn;
  } catch (e) {}
};

function* connection(action) {
  const connection = yield call(setupConnection, action.token);
  if (connection != null) {
    yield put(setConnection(connection));
  } else {
    // big error. handle this (log removed)
  }
}

const sendFeedback = async (connection, request) => {
  return await connection.invoke('sendFeedback', request, null);
};

function* sendFeedbackSaga(action) {
  const connection = yield select(selectConnection);
  try {
    if (isConnectionReady(connection)) {
      const graph = yield select(selectGraph);
      const filters = yield select(selectFilters);
      const contextIds = yield select(selectContextIds);
      const contexts = yield select(selectContextsObject);
      const { title, details, systemInfo } = action.payload;

      // Build context data for feedback
      const ids = contextIds ? Object.keys(contextIds) : [];
      const contextData = ids.map((id) => {
        const context = contexts?.[id]?.value;
        return {
          id,
          name: context?.displayValue || context?.value || context?.name || context?.label || id,
        };
      });

      const system = JSON.stringify({
        //graph: graph,
        filters: filters,
        contexts: contextData,
      });
      const request = {
        title,
        reproSteps: details,
        systemInfo: system,
      };
      const res = yield call(sendFeedback, connection, request);
      if (res && res !== null) {
        yield put(hideFeedbackModal('success'));
      } else {
        yield put(
          setFeedBackError(
            'Oops! Something went wrong! Please try again later',
          ),
        );
      }
    }
  } catch (e) {
    yield put(
      setFeedBackError('Oops! Something went wrong! Please try again later'),
    );
  }
}

// SignalR call to flag a node
const flagNode = async (connection, nodeId, description, category) => {
  // Call the SignalR hub method 'flagNode'
  return await connection.invoke('flagNode', nodeId, description, category);
};

function* sendFlagSaga(action) {
  const connection = yield select(selectConnection);
  try {
    if (isConnectionReady(connection)) {
      const { nodeId, description, category } = action.payload;
      
      const res = yield call(flagNode, connection, nodeId, description, category);
      
      if (res && res !== null) {
        yield put(hideFlagModal('success'));
      } else {
        yield put(setFlagError('Failed to submit flag. Please try again.'));
      }
    }
  } catch (e) {
    console.error('Flag error:', e);
    yield put(setFlagError('An error occurred. Please try again.'));
  }
}

// SignalR call to flag an edge
const flagEdge = async (connection, edgeId, description, category, edgeContext) => {
  // Call the SignalR hub method 'flagEdge'
  // Note: edgeContext is optional; backend may extract some fields from edgeId or accept the full context
  return await connection.invoke('flagEdge', edgeId, description, category, edgeContext);
};

function* sendEdgeFlagSaga(action) {
  const connection = yield select(selectConnection);
  try {
    if (isConnectionReady(connection)) {
      const { edgeId, description, category, edgeContext } = action.payload;
      
      const res = yield call(flagEdge, connection, edgeId, description, category, edgeContext);
      
      if (res && res !== null) {
        yield put(hideEdgeFlagModal('success'));
      } else {
        yield put(setEdgeFlagError('Failed to submit flag. Please try again.'));
      }
    }
  } catch (e) {
    console.error('Edge flag error:', e);
    yield put(setEdgeFlagError('An error occurred. Please try again.'));
  }
}

function* coreSaga() {
  yield takeLatest(SETUP_SIGNALR_CONNECTION, connection);
  yield takeLatest(SEND_FEEDBACK, sendFeedbackSaga);
  yield takeEvery(SEND_FLAG, sendFlagSaga);
  yield takeEvery(SEND_EDGE_FLAG, sendEdgeFlagSaga);
}

export default coreSaga;
